import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Plus, Edit, RefreshCw, Building2, AlertTriangle, Trash2 } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { Dialog } from '@/components/ui/Dialog';
import { usePrograms, useCreateProgram, useUpdateProgram, useDeleteProgram } from '@/hooks/useImmobilier';
import { STATUT_PROGRAMME_LABELS, type StatutProgramme, type Programme } from '@/types';
import { formatMontant, formatNumber } from '@/utils/currency';
import { extractError } from '@/api/client';
import { useAuthStore } from '@/stores/authStore';
import { demander } from '@/components/ui/Fenetres';

const STATUT_BADGE: Record<StatutProgramme, string> = {
  prospection: 'ds-bd ds-bd-n',
  en_cours:    'ds-bd ds-bd-i',
  livre:       'ds-bd ds-bd-s',
  suspendu:    'ds-bd ds-bd-w',
  annule:      'ds-bd ds-bd-d',
};

const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

export const ProgrammesPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [statut, setStatut]         = useState<StatutProgramme | ''>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing]       = useState<Programme | undefined>();
  const [error, setError]           = useState<string | null>(null);

  const { data, isLoading, refetch } = usePrograms({ statut: statut || undefined, limit: 100 });
  const programmes = data?.data ?? [];
  const remove = useDeleteProgram();

  const openCreate = () => { setEditing(undefined); setDialogOpen(true); };
  const openEdit   = (p: Programme) => { setEditing(p); setDialogOpen(true); };

  const onRemove = async (id: string, code: string) => {
    if (!(await demander.confirmer({ titre: `Supprimer le programme "${code}" ?`, message: 'Les lots associés deviendront isolés.', confirmer: 'Supprimer', danger: true }))) return;
    setError(null);
    try { await remove.mutateAsync(id); }
    catch (err) { setError(extractError(err).message); }
  };

  const valeurPortefeuille = programmes.reduce((s, p) => s + Number(BigInt(p.budgetTotalCents)) / 100, 0);
  const enCours = programmes.filter((p) => p.statut === 'en_cours').length;

  return (
    <>
      <Topbar
        title="Programmes immobiliers"
        subtitle={filiale ? `${programmes.length} programme(s) · ${filiale.fullName}` : 'Programmes'}
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void refetch()}><RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} /></button>
            <button className="ds-btn ds-btn-p" onClick={openCreate}><Plus size={14} /> Nouveau programme</button>
          </>
        }
      />
      <div className="ds-content">
        {error && <div className="ds-alert-strip ds-alert-d" style={{ marginBottom: 16 }}><span className="ds-alert-ico"><AlertTriangle size={18} /></span><div className="ds-alert-txt">{error}</div></div>}

        <div className="ds-kpi-row" style={{ marginBottom: 16 }}>
          <div className="ds-kr kr-c1"><div className="ds-kr-ico">🏗️</div><div className="ds-kr-val">{programmes.length}</div><div className="ds-kr-lbl">Programmes</div></div>
          <div className="ds-kr kr-c3"><div className="ds-kr-ico">🚀</div><div className="ds-kr-val">{enCours}</div><div className="ds-kr-lbl">En cours</div></div>
          <div className="ds-kr kr-c2"><div className="ds-kr-ico">💰</div><div className="ds-kr-val">{formatNumber(valeurPortefeuille)}</div><div className="ds-kr-lbl">Budget total (FCFA)</div></div>
        </div>

        <div className="ds-filters">
          <select className="ds-fi" value={statut} onChange={(e) => setStatut(e.target.value as StatutProgramme | '')} style={{ width: 'auto' }}>
            <option value="">Tous statuts</option>
            {(Object.keys(STATUT_PROGRAMME_LABELS) as StatutProgramme[]).map((s) => <option key={s} value={s}>{STATUT_PROGRAMME_LABELS[s]}</option>)}
          </select>
        </div>

        <div className="ds-table-panel">
          <div className="ds-table-header"><div><div className="ds-table-title">Programmes</div><div className="ds-table-sub">{programmes.length} programme{programmes.length > 1 ? 's' : ''}</div></div></div>
          <table className="ds-tbl">
            <thead><tr>
              <th style={{ width: 130 }}>Code</th>
              <th>Nom / Type</th>
              <th>Localisation</th>
              <th className="ds-am" style={{ width: 110 }}>Budget FCFA</th>
              <th className="ds-am" style={{ width: 80 }}>Lots</th>
              <th style={{ width: 110 }}>Livraison</th>
              <th style={{ width: 110 }}>Statut</th>
              <th style={{ width: 80 }}></th>
            </tr></thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: 'var(--tx-3)' }}>Chargement…</td></tr>}
              {!isLoading && programmes.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 48 }}>
                  <Building2 size={28} style={{ margin: '0 auto 8px', color: 'var(--tx-3)' }} />
                  <p style={{ fontSize: 13, color: 'var(--tx-3)' }}>Aucun programme</p>
                  <button className="ds-btn ds-btn-p ds-btn-sm" style={{ marginTop: 12 }} onClick={openCreate}><Plus size={12} /> Créer le premier programme</button>
                </td></tr>
              )}
              {!isLoading && programmes.map((p) => (
                <tr key={p.id}>
                  <td><span className="ds-ref-code">{p.code}</span></td>
                  <td>
                    <div className="ds-entry-label">{p.nom}</div>
                    {p.typeProgramme && <div className="ds-entry-sub">{p.typeProgramme}</div>}
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {p.quartier ?? '—'}
                    {p.ville && <div style={{ fontSize: 10, color: 'var(--tx-3)' }}>{p.ville}</div>}
                  </td>
                  <td className="ds-am" style={{ fontWeight: 600 }}>{formatMontant(p.budgetTotalCents)}</td>
                  <td className="ds-am">{p._count?.lots ?? 0}{p.nbLotsTotal ? ` / ${p.nbLotsTotal}` : ''}</td>
                  <td style={{ fontSize: 12, color: 'var(--tx-2)' }}>{fmtDate(p.dateLivraisonPrevue)}</td>
                  <td><span className={STATUT_BADGE[p.statut]}>{STATUT_PROGRAMME_LABELS[p.statut]}</span></td>
                  <td><div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => openEdit(p)} className="ds-row-btn" title="Modifier"><Edit size={13} /></button>
                    <button onClick={() => onRemove(p.id, p.code)} className="ds-row-btn" title="Supprimer" disabled={remove.isPending}><Trash2 size={13} /></button>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <ProgrammeFormDialog open={dialogOpen} onClose={() => setDialogOpen(false)} programme={editing} />
    </>
  );
};

const ProgrammeFormDialog = ({ open, onClose, programme }: { open: boolean; onClose: () => void; programme?: Programme }) => {
  const isEdit = !!programme;
  const [code, setCode]                   = useState('');
  const [nom, setNom]                     = useState('');
  const [typeProgramme, setTypeProgramme] = useState('');
  const [adresse, setAdresse]             = useState('');
  const [quartier, setQuartier]           = useState('');
  const [ville, setVille]                 = useState('');
  const [surface, setSurface]             = useState('');
  const [budget, setBudget]               = useState('');
  const [budgetDepense, setBudgetDepense] = useState('');
  const [dateDebut, setDateDebut]         = useState('');
  const [dateLivraison, setDateLivraison] = useState('');
  const [statut, setStatut]               = useState<StatutProgramme>('prospection');
  const [nbLots, setNbLots]               = useState('');
  const [notes, setNotes]                 = useState('');
  const [error, setError]                 = useState<string | null>(null);

  const create = useCreateProgram();
  const update = useUpdateProgram();

  useEffect(() => {
    if (!open) return;
    if (programme) {
      setCode(programme.code); setNom(programme.nom); setTypeProgramme(programme.typeProgramme ?? '');
      setAdresse(programme.adresse ?? ''); setQuartier(programme.quartier ?? ''); setVille(programme.ville ?? '');
      setSurface(programme.surfaceTerrainM2 ?? '');
      setBudget(String(Number(BigInt(programme.budgetTotalCents)) / 100));
      setBudgetDepense(String(Number(BigInt(programme.budgetDepenseCents)) / 100));
      setDateDebut(programme.dateDebut?.slice(0, 10) ?? '');
      setDateLivraison(programme.dateLivraisonPrevue?.slice(0, 10) ?? '');
      setStatut(programme.statut);
      setNbLots(programme.nbLotsTotal?.toString() ?? '');
      setNotes(programme.notes ?? '');
    } else {
      setCode(''); setNom(''); setTypeProgramme(''); setAdresse(''); setQuartier(''); setVille('');
      setSurface(''); setBudget(''); setBudgetDepense('0'); setDateDebut(''); setDateLivraison('');
      setStatut('prospection'); setNbLots(''); setNotes('');
    }
    setError(null);
  }, [open, programme]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        code: code.trim(), nom: nom.trim(),
        typeProgramme: typeProgramme.trim() || undefined,
        adresse: adresse.trim() || undefined, quartier: quartier.trim() || undefined, ville: ville.trim() || undefined,
        surfaceTerrainM2: surface ? parseFloat(surface) : undefined,
        budgetTotalCents:   Math.round(parseFloat(budget || '0') * 100),
        budgetDepenseCents: Math.round(parseFloat(budgetDepense || '0') * 100),
        dateDebut:           dateDebut || undefined,
        dateLivraisonPrevue: dateLivraison || undefined,
        statut,
        nbLotsTotal: nbLots ? parseInt(nbLots, 10) : undefined,
        notes: notes.trim() || undefined,
      };
      if (isEdit && programme) await update.mutateAsync({ id: programme.id, payload });
      else await create.mutateAsync(payload);
      onClose();
    } catch (err) { setError(extractError(err).message); }
  };

  const canSubmit = code.trim() && nom.trim() && parseFloat(budget || '0') >= 0;

  return (
    <Dialog open={open} onClose={onClose} title={isEdit ? `Modifier — ${programme?.code}` : 'Nouveau programme immobilier'} subtitle="David & Co — promotion immobilière" size="lg">
      <form onSubmit={onSubmit} className="space-y-3">
        {error && <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"><AlertTriangle className="inline size-3 mr-1" />{error}</div>}

        <div className="grid grid-cols-3 gap-3">
          <Field label="Code *"><input value={code} onChange={(e) => setCode(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="PRG-2026-001" /></Field>
          <Field label="Type"><input value={typeProgramme} onChange={(e) => setTypeProgramme(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="résidence" /></Field>
          <Field label="Statut">
            <select value={statut} onChange={(e) => setStatut(e.target.value as StatutProgramme)} className="ds-fi" style={{ width: '100%' }}>
              {(Object.keys(STATUT_PROGRAMME_LABELS) as StatutProgramme[]).map((s) => <option key={s} value={s}>{STATUT_PROGRAMME_LABELS[s]}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Nom *"><input value={nom} onChange={(e) => setNom(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="Résidence Cocody Riviera Golf" /></Field>

        <Field label="Adresse"><input value={adresse} onChange={(e) => setAdresse(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Quartier"><input value={quartier} onChange={(e) => setQuartier(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Cocody" /></Field>
          <Field label="Ville"><input value={ville} onChange={(e) => setVille(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="Abidjan" /></Field>
          <Field label="Surface terrain (m²)"><input type="number" min="0" step="0.01" value={surface} onChange={(e) => setSurface(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Budget total (FCFA) *"><input type="number" min="0" step="1" value={budget} onChange={(e) => setBudget(e.target.value)} className="ds-fi" required style={{ width: '100%' }} placeholder="150000000" /></Field>
          <Field label="Budget déjà dépensé"><input type="number" min="0" step="1" value={budgetDepense} onChange={(e) => setBudgetDepense(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="0" /></Field>
          <Field label="Nb lots prévus"><input type="number" min="1" value={nbLots} onChange={(e) => setNbLots(e.target.value)} className="ds-fi" style={{ width: '100%' }} placeholder="24" /></Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Date début"><input type="date" value={dateDebut} onChange={(e) => setDateDebut(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
          <Field label="Livraison prévue"><input type="date" value={dateLivraison} onChange={(e) => setDateLivraison(e.target.value)} className="ds-fi" style={{ width: '100%' }} /></Field>
        </div>

        <Field label="Notes"><textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="ds-fi" style={{ width: '100%' }} /></Field>

        <div className="flex justify-end gap-2 border-t border-gray-100 pt-3">
          <button type="button" className="ds-btn ds-btn-g" onClick={onClose}>Annuler</button>
          <button type="submit" className="ds-btn ds-btn-p" disabled={!canSubmit || create.isPending || update.isPending}>{isEdit ? 'Enregistrer' : 'Créer le programme'}</button>
        </div>
      </form>
    </Dialog>
  );
};

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div><label className="mb-1 block text-xs font-medium text-gray-600">{label}</label>{children}</div>
);
